import { type FC, useEffect, useMemo, useState } from "react"

import type { BlogPostsBlockLayout, LandingPageBlogPostsBlock } from "@forento/core/models/landing"
import {
	blogPostsBlockLayouts,
	blogPostsBlockMaxPostCount,
	landingPageCopyCharacterLimits,
} from "@forento/core/schemas/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import Link from "~/components/Link"
import routes from "~/utilities/routes"

import { getBlogPostsBlockPosts, usePublishedBlogPosts } from "../preview/BlogPostsBlockPreview"
import EditableField from "./EditableField"
import EditorShell from "./EditorShell"

type Props = {
	block: LandingPageBlogPostsBlock
	onChange: (block: LandingPageBlogPostsBlock) => void
	onClose: () => void
}

const layoutLabels: Record<BlogPostsBlockLayout, string> = { grid: "Grid", list: "List" }

const BlogPostsBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const publishedPosts = usePublishedBlogPosts()
	const [title, setTitle] = useState(block.content.title ?? "")
	const [tag, setTag] = useState(block.content.tag)
	const [postCount, setPostCount] = useState(block.content.postCount)
	const [layout, setLayout] = useState(block.content.layout)

	useEffect(() => {
		onChange({
			type: "blog-posts",
			content: { title: title.trim() || undefined, ...(tag !== undefined ? { tag } : {}), postCount, layout },
		})
	}, [layout, onChange, postCount, tag, title])

	const tags = useMemo(() => {
		const tagsByKey = new Map<string, string>()
		for (const post of publishedPosts ?? []) {
			for (const postTag of post.tags) {
				if (!tagsByKey.has(postTag.toLowerCase())) tagsByKey.set(postTag.toLowerCase(), postTag)
			}
		}
		// Keeps a tag whose posts were unpublished selectable, so opening the editor doesn't change the block.
		if (tag !== undefined && !tagsByKey.has(tag.toLowerCase())) tagsByKey.set(tag.toLowerCase(), tag)
		return [...tagsByKey.values()].toSorted((a, b) => a.localeCompare(b))
	}, [publishedPosts, tag])

	// The block matches tags case-insensitively, so a stored tag may differ in casing from the posts' tag.
	const selectedTag = tags.find(option => option.toLowerCase() === tag?.toLowerCase()) ?? ""

	const shownPostCount =
		publishedPosts === undefined ? null : getBlogPostsBlockPosts(publishedPosts, { postCount, layout, tag }).length

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder="Blog section title"
						className="mb-12 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<div className="mb-8 grid gap-5 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5 sm:grid-cols-3">
						<label className="block">
							<span className="text-sm font-semibold text-gray-900">Number of posts</span>
							<select
								value={postCount}
								onChange={event => setPostCount(Number(event.target.value))}
								className="mt-2 block w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm"
							>
								{Array.from({ length: blogPostsBlockMaxPostCount }, (_, index) => index + 1).map(
									count => (
										<option key={count} value={count}>
											{count}
										</option>
									),
								)}
							</select>
						</label>
						<label className="block">
							<span className="text-sm font-semibold text-gray-900">Tag</span>
							<select
								value={selectedTag}
								onChange={event => setTag(event.target.value || undefined)}
								className="mt-2 block w-full cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm"
							>
								<option value="">All posts</option>
								{tags.map(option => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</label>
						<div>
							<span className="text-sm font-semibold text-gray-900">Layout</span>
							<div className="mt-2 flex overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
								{blogPostsBlockLayouts.map((option, index) => (
									<button
										key={option}
										type="button"
										aria-pressed={layout === option}
										onClick={() => setLayout(option)}
										className={cn(
											"flex-1 cursor-pointer px-4 py-2 text-sm font-semibold transition",
											index > 0 && "border-l border-gray-200",
											layout === option
												? "bg-gray-900 text-white"
												: "text-gray-700 hover:bg-gray-50",
										)}
									>
										{layoutLabels[option]}
									</button>
								))}
							</div>
						</div>
					</div>
					<p className="text-sm text-gray-500">
						{shownPostCount === null
							? "Loading blog posts..."
							: shownPostCount === 0
								? "No published posts match yet, so the block stays hidden on your website until one does."
								: `Shows your ${shownPostCount === 1 ? "newest post" : `${shownPostCount} newest posts`}${tag !== undefined ? ` tagged “${tag}”` : ""}, linking to the full blog.`}{" "}
						<Link to={routes.main.websiteBlog()} className="font-semibold text-indigo-600 hover:underline">
							Manage blog posts
						</Link>
					</p>
				</div>
			</section>
		</EditorShell>
	)
}

export default BlogPostsBlockEditor
